import { Head, Link, useForm } from '@inertiajs/react'
import Layout from '../../components/Layout'
import MemberPicker from '../../components/teams/MemberPicker'

interface OrgUser {
  id: number
  full_name: string
  email: string
  initials: string
}

interface Props {
  org_users: OrgUser[]
}

export default function TeamsNew({ org_users }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    description: '',
    is_private: false,
    member_ids: [] as number[],
  })

  const handleSubmit = () => {
    post('/teams')
  }

  return (
    <Layout>
      <Head title="Create Team — Pinnacle" />
      <div className="max-w-2xl mx-auto px-6 py-8">

        <nav className="text-sm mb-6 text-text-muted">
          <Link href="/teams" className="hover:text-text-primary">Teams</Link>
          <span className="mx-2">›</span>
          <span className="text-text-primary">New Team</span>
        </nav>

        <h1 className="text-2xl font-semibold text-text-primary font-heading mb-6">Create a New Team</h1>

        <div className="bg-white rounded-lg border border-stone shadow-sm p-6 space-y-6">

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Team Name <span className="text-pinnacle-red">*</span>
            </label>
            <input
              type="text"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              placeholder="e.g. Leadership Team, Marketing, Sales"
              className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none"
              autoFocus
            />
            {errors.name && <p className="text-pinnacle-red text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Description
            </label>
            <textarea
              value={data.description}
              onChange={e => setData('description', e.target.value)}
              placeholder="What does this team own or focus on?"
              rows={3}
              className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Visibility
            </label>
            <div className="flex gap-4">
              {[
                { value: false, label: 'Public', description: 'All organization members can see this team' },
                { value: true, label: 'Private', description: 'Only members can see this team' },
              ].map(option => (
                <label
                  key={String(option.value)}
                  className={[
                    'flex-1 border rounded-lg p-4 cursor-pointer transition-colors',
                    data.is_private === option.value
                      ? 'border-pinnacle-red bg-[rgba(237,28,36,0.15)]'
                      : 'border-stone hover:border-charcoal'
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={data.is_private === option.value}
                    onChange={() => setData('is_private', option.value)}
                  />
                  <p className="font-semibold text-sm text-text-primary">{option.label}</p>
                  <p className="text-sm text-text-secondary mt-1">{option.description}</p>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Members
            </label>
            <p className="text-sm text-text-secondary mb-3">You will be added as Team Leader automatically.</p>
            <MemberPicker
              orgUsers={org_users}
              selectedIds={data.member_ids}
              onChange={(ids) => setData('member_ids', ids)}
            />
          </div>

        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Link href="/teams" className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors">
            Cancel
          </Link>
          <button
            onClick={handleSubmit}
            disabled={processing || !data.name.trim()}
            className="px-5 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
          >
            {processing ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      </div>
    </Layout>
  )
}
